import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser, SignInButton } from '@clerk/clerk-react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Slider,
} from '@mui/material';
import {
  Plus as PlusIcon,
  X as XIcon,
  MapPin as LocationIcon,
  DollarSign as MoneyIcon,
  Folder as FolderIcon,
  FileText as FileTextIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  ThumbsUp as ThumbsUpIcon,
  ThumbsDown as ThumbsDownIcon,
  Linkedin as LinkedInIcon,
  MessageCircle as MessageCircleIcon,
  Wifi as WifiIcon,
  Laptop as LaptopIcon,
  Building2 as BuildingIcon,
} from 'lucide-react';
import {
  searchJobs,
  importJob,
  getProfile,
  getSubscriptionStatus,
  dismissTitle,
  submitJobFeedback,
  saveSearchPreferences,
  getLinkedInRecruiterStatus,
  claimLinkedInRecruiterAccess,
  getApiErrorMessage,
} from '../services/api';
import { fbTrack } from '../services/pixel';
import { trackFunnelEvent, rememberSignInIntent } from '../services/funnel';
import LocationPicker from './LocationPicker';
import QuickApply from './QuickApply';
import validator from 'validator';
import { layoutPatterns, designSystem } from '../theme';

const ANON_RECRUITER_LIMIT = 3;
const ANON_RECRUITER_STORAGE_KEY = 'jjj_anon_recruiter_usage';
const DEFAULT_ANON_RADIUS = 500;
const WORK_TYPE_OPTIONS = [
  { id: 'remote', label: 'Remote', Icon: WifiIcon },
  { id: 'hybrid', label: 'Hybrid', Icon: LaptopIcon },
  { id: 'onsite', label: 'On-site', Icon: BuildingIcon },
];
const ALL_WORK_TYPE_IDS = WORK_TYPE_OPTIONS.map(option => option.id);

const createAllWorkTypeSet = () => new Set(ALL_WORK_TYPE_IDS);
const serializeWorkTypes = (workTypeFilter) => (
  [...workTypeFilter].filter(type => ALL_WORK_TYPE_IDS.includes(type)).sort()
);

const canUseAnonymousRecruiterStorage = () => {
  try {
    const probeKey = `${ANON_RECRUITER_STORAGE_KEY}__probe`;
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
};

const getLocalDayKey = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getAnonymousRecruiterStatus = () => {
  const day = getLocalDayKey();
  if (!canUseAnonymousRecruiterStorage()) {
    return {
      is_premium: false,
      daily_limit: ANON_RECRUITER_LIMIT,
      used_today: ANON_RECRUITER_LIMIT,
      remaining_today: 0,
      storage_available: false,
    };
  }
  try {
    const raw = localStorage.getItem(ANON_RECRUITER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const usedToday = parsed?.day === day ? Number(parsed.used || 0) : 0;
    return {
      is_premium: false,
      daily_limit: ANON_RECRUITER_LIMIT,
      used_today: usedToday,
      remaining_today: Math.max(ANON_RECRUITER_LIMIT - usedToday, 0),
      storage_available: true,
    };
  } catch {
    return {
      is_premium: false,
      daily_limit: ANON_RECRUITER_LIMIT,
      used_today: ANON_RECRUITER_LIMIT,
      remaining_today: 0,
      storage_available: false,
    };
  }
};

const consumeAnonymousRecruiterSlot = () => {
  const current = getAnonymousRecruiterStatus();
  if (!current.storage_available || current.remaining_today <= 0) {
    return null;
  }

  const nextUsed = current.used_today + 1;
  try {
    localStorage.setItem(
      ANON_RECRUITER_STORAGE_KEY,
      JSON.stringify({ day: getLocalDayKey(), used: nextUsed })
    );
  } catch {
    return null;
  }

  return {
    is_premium: false,
    daily_limit: ANON_RECRUITER_LIMIT,
    used_today: nextUsed,
    remaining_today: Math.max(ANON_RECRUITER_LIMIT - nextUsed, 0),
    storage_available: true,
  };
};

const cleanLinkedInQueryPart = (value) => {
  if (!value) return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, 80);
};

const getLinkedInRecruiterUrl = (jobOrCompany) => {
  const job = typeof jobOrCompany === 'string' ? { company: jobOrCompany } : (jobOrCompany || {});
  const company = cleanLinkedInQueryPart(job.company);
  if (!company) return 'https://www.linkedin.com';

  const title = cleanLinkedInQueryPart(job.title);
  const location = cleanLinkedInQueryPart(job.location);
  const category = cleanLinkedInQueryPart(job.category);
  const parts = [
    `"${company}"`,
    '"recruiter" OR "talent acquisition" OR "sourcer" OR "hiring manager"',
  ];

  if (title) parts.push(`"${title}"`);
  if (category) parts.push(`"${category}"`);
  if (location && !location.toLowerCase().includes('remote')) parts.push(`"${location}"`);
  parts.push('"United States"');

  const query = encodeURIComponent(parts.join(' '));
  return `https://www.linkedin.com/search/results/people/?keywords=${query}`;
};

const ROLE_PRESETS = [
  {
    test: /software|backend|full.?stack|api|platform/i,
    titles: ['Software Engineer', 'Backend Engineer', 'Platform Engineer', 'API Engineer'],
  },
  {
    test: /front.?end|frontend|react|ui/i,
    titles: ['Frontend Engineer', 'React Engineer', 'UI Engineer', 'Software Engineer'],
  },
  {
    test: /data|analytics|analyst/i,
    titles: ['Data Analyst', 'Data Engineer', 'Analytics Engineer', 'Business Intelligence Analyst'],
  },
  {
    test: /project manager|program manager|scrum/i,
    titles: ['Project Manager', 'Program Manager', 'Technical Project Manager', 'Scrum Master'],
  },
];

const getTargetRoles = (title, fallbackTitles = []) => {
  const cleanTitle = cleanLinkedInQueryPart(title);
  const preset = ROLE_PRESETS.find(({ test }) => test.test(cleanTitle));
  const titles = [cleanTitle, ...(preset?.titles || []), ...fallbackTitles]
    .map(cleanLinkedInQueryPart)
    .filter(Boolean);
  return [...new Set(titles)].slice(0, 4);
};

const buildCandidateSearchQuery = ({ roles, location, skills = [] }) => {
  const cleanRoles = [...new Set((roles || []).map(cleanLinkedInQueryPart).filter(Boolean))].slice(0, 4);
  const roleQuery = cleanRoles.length > 1
    ? `(${cleanRoles.map(role => `"${role}"`).join(' OR ')})`
    : (cleanRoles[0] || '');
  const keywordParts = (skills || []).map(cleanLinkedInQueryPart).filter(Boolean).slice(0, 4);
  return [roleQuery, cleanLinkedInQueryPart(location), ...keywordParts].filter(Boolean).join(' AND ');
};

const getLinkedInCandidateUrl = (query) => (
  `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(query)}`
);

const LINKEDIN_ACCESS_TIMEOUT_MS = 5000;

const showLinkedInOpeningPage = (popup) => {
  if (!popup || popup.closed) return;
  try {
    popup.document.title = 'Opening LinkedIn...';
    popup.document.body.innerHTML = `
      <main style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 32px; color: #1f2937;">
        <h1 style="font-size: 20px; margin: 0 0 8px;">Opening LinkedIn recruiter search...</h1>
        <p style="margin: 0; color: #4b5563;">This tab will redirect automatically.</p>
      </main>
    `;
  } catch {
    // Some browsers restrict writing to the popup; the redirect path still works.
  }
};

const withLinkedInAccessTimeout = (promise) => (
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('linkedin_access_timeout')), LINKEDIN_ACCESS_TIMEOUT_MS);
    }),
  ])
);

function JobSearch({ onJobImported, onContextUpdate, searchResults, setSearchResults, searchMeta, setSearchMeta, userRole, initData, initLoading = false, initErrorStatus = null }) {
  const pageLocation = useLocation();
  const { isSignedIn, isLoaded, user } = useUser();
  const isMatcherMode = userRole === 'matcher';
  const trackingPath = isMatcherMode ? '/jobmatcher' : '/jobseeker';
  const PREFS_KEY = 'jjj_search_prefs';

  const [profile, setProfile] = useState(null);
  const [suggestedTitles, setSuggestedTitles] = useState(null);
  const [selectedTitles, setSelectedTitles] = useState(new Set());
  const [customTitle, setCustomTitle] = useState('');
  const [customTitles, setCustomTitles] = useState([]);
  // Signed-out users get their last location from localStorage.
  // Signed-in users get it from MongoDB (search_preferences) in loadData().
  const _savedLoc = (() => {
    try { return JSON.parse(localStorage.getItem('jjj_last_location') || 'null'); } catch { return null; }
  })();
  const [locationData, setLocationData] = useState({
    location: _savedLoc?.location || 'new york',
    lat:      _savedLoc?.lat      || null,
    lng:      _savedLoc?.lng      || null,
    radius:   _savedLoc?.radius   || (isSignedIn ? 25 : DEFAULT_ANON_RADIUS),
  });
  const locSaveTimer = useRef(null);
  // Use lifted state from App.js for results (persists across navigation)
  const results = searchResults || [];
  const setResults = setSearchResults;
  const totalCount = searchMeta?.totalCount || 0;
  const setTotalCount = (count) => setSearchMeta(prev => ({ ...prev, totalCount: count }));
  const searchedTitles = searchMeta?.searchedTitles || [];
  const setSearchedTitles = (titles) => setSearchMeta(prev => ({ ...prev, searchedTitles: titles }));

  const [salaryRange, setSalaryRange] = useState([0, 200000]);
  const [sliderRange, setSliderRange] = useState([0, 200000]);
  const [workTypeFilter, setWorkTypeFilter] = useState(createAllWorkTypeSet);
  const prefsSaveTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState({});
  const [jobFeedback, setJobFeedback] = useState({}); // { [source_id]: 'up' | 'down' }
  const [isPro, setIsPro] = useState(false);
  const [linkedInStatus, setLinkedInStatus] = useState({
    is_premium: false,
    daily_limit: null,
    used_today: 0,
    remaining_today: null,
    storage_available: true,
  });
  const [copyMessage, setCopyMessage] = useState(null);
  const autoSearchTimerRef = useRef(null);
  const lastSearchSignatureRef = useRef('');
  const seededStarterSearchRef = useRef(false);
  const activeSearchRequestRef = useRef(0);

  // Clear PERSONAL data when user signs out (but keep search working)
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      setProfile(null);
      setSuggestedTitles(null);
      setSelectedTitles(new Set());
      setCustomTitles([]);
      setCustomTitle('');
      setSalaryRange([0, 200000]);
      setSliderRange([0, 200000]);
      setWorkTypeFilter(createAllWorkTypeSet());
      setLocationData(prev => ({
        ...prev,
        radius: _savedLoc?.radius || DEFAULT_ANON_RADIUS,
      }));
      setLoading(false);
      try { localStorage.removeItem(PREFS_KEY); } catch {}
    }
  }, [isSignedIn, isLoaded, _savedLoc?.radius]);

  // Load profile and suggested titles
  // Waits for initData from App.js to avoid duplicate API calls
  useEffect(() => {
    let cancelled = false;
    if (isLoaded && isSignedIn && initData) {
      loadData(cancelled);
    } else if (isLoaded && isSignedIn && initLoading) {
      setLoading(true);
    } else if (isLoaded && isSignedIn && initErrorStatus === 429) {
      // Avoid making more bootstrap calls while the backend is throttling.
      setLoading(false);
    } else if (isLoaded && isSignedIn && !initData) {
      // If app init fails or is delayed, fall back to direct profile calls
      // instead of leaving the route on an infinite spinner.
      loadData(cancelled);
    } else if (isLoaded && !isSignedIn) {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [isMatcherMode, isSignedIn, isLoaded, initData, initLoading, initErrorStatus]);

  // Update navbar context when data changes
  useEffect(() => {
    onContextUpdate?.({
      profile,
      resultsCount: totalCount,
      suggestedTitles,
      isPro,
    });
  }, [profile, totalCount, suggestedTitles, isPro, onContextUpdate]);

  // Auto-save search preferences whenever search settings change
  useEffect(() => {
    if (isSignedIn && profile) {
      const prefs = {
        salary_min: salaryRange[0],
        salary_max: salaryRange[1],
        work_types: [...workTypeFilter],
        selected_titles: [...selectedTitles],
        custom_titles: customTitles,
      };
      // For signed-in users, debounce the MongoDB write by 1.5s
      if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
      prefsSaveTimer.current = setTimeout(() => {
        saveSearchPreferences(prefs).catch(err =>
          console.error('Failed to save search preferences:', err)
        );
      }, 1500);
    }
    return () => { if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current); };
  }, [salaryRange, workTypeFilter, selectedTitles, customTitles, isSignedIn, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async (cancelled = false) => {
    setLoading(true);
    try {
      // Use cached initData from App.js if available (avoids duplicate API calls)
      let profileData, isPremium;
      if (initData?.profile) {
        profileData = initData.profile;
        isPremium = initData.is_premium || false;
      } else {
        // Fallback to individual calls if initData not ready yet
        const [p, subStatus] = await Promise.all([
          getProfile(),
          user?.id ? getSubscriptionStatus().catch(() => ({ is_premium: false })) : Promise.resolve({ is_premium: false })
        ]);
        profileData = p;
        isPremium = subStatus?.is_premium || false;
      }
      
      if (cancelled) return;
      setProfile(profileData);
      setIsPro(isPremium);

      // Restore saved search preferences — MongoDB is the source of truth for signed-in users
      const savedPrefs = profileData?.search_preferences;
      if (savedPrefs) {
        const min = savedPrefs.salary_min ?? 0;
        const max = savedPrefs.salary_max ?? 200000;
        setSalaryRange([min, max]);
        setSliderRange([min, max]);
        // Start each signed-in session broad; users can narrow the filter from all work types.
        setWorkTypeFilter(createAllWorkTypeSet());
        if (Array.isArray(savedPrefs.custom_titles)) {
          setCustomTitles(savedPrefs.custom_titles);
        }
        // Restore location from MongoDB if the user has explicitly saved one
        if (savedPrefs.last_location) {
          setLocationData(prev => ({
            ...prev,
            location: savedPrefs.last_location,
            radius:   savedPrefs.last_radius ?? prev.radius,
          }));
        }
      }

      if (isMatcherMode) {
        setSuggestedTitles([]);
        return;
      }

      if (profileData) {
        // Only fall back to the resume/profile location if the user has never
        // explicitly chosen a search location (first-time users)
        const hasLocationPref = Boolean(profileData?.search_preferences?.last_location);
        if (!hasLocationPref) {
          const profileLocation = profileData.desired_locations?.[0] || profileData.location || 'new york';
          setLocationData(prev => ({ ...prev, location: profileLocation }));
        }

        // Get dismissed titles to filter them out
        const dismissed = new Set(profileData.dismissed_titles || []);

        let titlesToSearch = [];
        
        // Use AI-suggested titles if available, filtering out dismissed ones
        // Show all titles in the UI but only pre-select top 5 for speed
        const MAX_PRESELECT = 5;
        if (profileData.suggested_titles && Array.isArray(profileData.suggested_titles) && profileData.suggested_titles.length > 0) {
          const filtered = profileData.suggested_titles.filter(t => !dismissed.has(t));
          const savedSelected = new Set(savedPrefs?.selected_titles || []);
          const restoredSelection = filtered.filter(t => savedSelected.has(t));
          setSuggestedTitles(filtered);
          if (restoredSelection.length > 0) {
            setSelectedTitles(new Set(restoredSelection));
            titlesToSearch = restoredSelection;
          } else {
            setSelectedTitles(new Set(filtered.slice(0, MAX_PRESELECT)));
            titlesToSearch = filtered.slice(0, MAX_PRESELECT);
          }
        } 
        // Otherwise use user's desired_titles
        else if (profileData.desired_titles && profileData.desired_titles.length > 0) {
          const filtered = profileData.desired_titles.filter(t => !dismissed.has(t));
          const savedSelected = new Set(savedPrefs?.selected_titles || []);
          const restoredSelection = filtered.filter(t => savedSelected.has(t));
          setSuggestedTitles(filtered);
          if (restoredSelection.length > 0) {
            setSelectedTitles(new Set(restoredSelection));
            titlesToSearch = restoredSelection;
          } else {
            setSelectedTitles(new Set(filtered.slice(0, MAX_PRESELECT)));
            titlesToSearch = filtered.slice(0, MAX_PRESELECT);
          }
        }
        // Fallback to building from profile
        else {
          const fallbackTitles = buildFallbackTitles(profileData);
          if (fallbackTitles.length > 0) {
            setSuggestedTitles(fallbackTitles);
            setSelectedTitles(new Set(fallbackTitles));
            titlesToSearch = fallbackTitles;
          }
        }

        // NOTE: Removed auto-search on profile load to save API calls
        // User must click "Search Jobs" button to search
        if (!titlesToSearch.length && profileData.title) {
          setCustomTitle(profileData.title);
        }
      }
    } catch (err) {
      if (!cancelled) console.error('Failed to load profile:', err);
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  const buildFallbackTitles = (profileData) => {
    const titles = [];
    if (profileData.desired_titles?.length > 0) {
      titles.push(...profileData.desired_titles);
    }
    if (profileData.title && !titles.includes(profileData.title)) {
      titles.push(profileData.title);
    }
    return titles.slice(0, 5);
  };

  const performMultiSearch = async (titles, searchLocation, searchRadius, page = 1, append = false) => {
    if (!titles || titles.length === 0) return;
    const requestId = append ? activeSearchRequestRef.current : activeSearchRequestRef.current + 1;

    if (!append) {
      activeSearchRequestRef.current = requestId;
    }

    if (!append) {
      setSearching(true);
      // Clear results immediately so user sees fresh search starting
      if (!append) setResults([]);
    }
    setError(null);
    setSearchedTitles(titles);

    // Progressive loading: show results as each title search completes
    // Instead of waiting for ALL titles, update UI as each resolves
    const jobMap = new Map();
    let runningTotal = 0;
    
    if (append) {
      results.forEach(job => jobMap.set(job.source_id, job));
    }

    const resultsPerPage = 100;
    const sanitizedLocation = validator.escape(validator.trim(searchLocation || 'new york'));
    const selectedWorkTypes = serializeWorkTypes(workTypeFilter);

    // Helper to update results progressively
    const updateResults = () => {
      if (requestId !== activeSearchRequestRef.current) return;

      const combinedResults = Array.from(jobMap.values()).sort((a, b) => {
        const scoreA = a.match_display_score ?? 0;
        const scoreB = b.match_display_score ?? 0;
        return scoreB - scoreA;
      });

      setResults(combinedResults.slice(0, 100));
      setTotalCount(runningTotal);
    };

    // Stagger searches in batches of 3 for speed — results stream in progressively
    const BATCH_SIZE = 3;
    const searchOneTitle = (title) =>
      searchJobs(
        {
          query: validator.escape(validator.trim(title)),
          location: sanitizedLocation,
          distance_miles: searchRadius,
          work_types: selectedWorkTypes,
          page: page,
          results_per_page: resultsPerPage,
        },
        isSignedIn ? user : null
      )
      .then(data => {
        if (requestId !== activeSearchRequestRef.current) {
          return data;
        }

        // Merge results as they arrive (progressive loading)
        if (data.jobs) {
          runningTotal += data.count || 0;
          data.jobs.forEach(job => {
            const existingJob = jobMap.get(job.source_id);
            if (existingJob) {
              existingJob.matched_titles = existingJob.matched_titles || [];
              if (!existingJob.matched_titles.includes(title)) {
                existingJob.matched_titles.push(title);
              }
            } else {
              job.matched_titles = [title];
              jobMap.set(job.source_id, job);
            }
          });
          // Update UI immediately with new results
          updateResults();
        }
        return data;
      })
      .catch(err => {
        if (requestId !== activeSearchRequestRef.current) {
          return { jobs: [], count: 0, error: err };
        }
        return { jobs: [], count: 0, error: err };
      });

    try {
      // Fire searches in batches of 3 — each batch streams results as they arrive
      for (let i = 0; i < titles.length; i += BATCH_SIZE) {
        const batch = titles.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map(searchOneTitle));
      }
      
      if (requestId !== activeSearchRequestRef.current) return;
    } catch (err) {
      if (requestId === activeSearchRequestRef.current) {
        setError(getApiErrorMessage(err, 'Job search is temporarily unavailable. Please try again in a moment.'));
      }
    } finally {
      if (requestId === activeSearchRequestRef.current) {
        setSearching(false);
      }
    }
  };

  const performAnonymousBrowseSearch = async (searchLocation, searchRadius, page = 1) => {
    const requestId = activeSearchRequestRef.current + 1;
    activeSearchRequestRef.current = requestId;
    setSearching(true);
    setResults([]);
    setError(null);
    setSearchedTitles([]);

    try {
      const selectedWorkTypes = serializeWorkTypes(workTypeFilter);
      const data = await searchJobs(
        {
          location: validator.escape(validator.trim(searchLocation || 'new york')),
          distance_miles: searchRadius,
          work_types: selectedWorkTypes,
          page,
          results_per_page: 100,
        },
        null
      );

      if (requestId !== activeSearchRequestRef.current) return;

      setResults(data.jobs || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      if (requestId === activeSearchRequestRef.current) {
        setError(getApiErrorMessage(err, 'Job search is temporarily unavailable. Please try again in a moment.'));
      }
    } finally {
      if (requestId === activeSearchRequestRef.current) {
        setSearching(false);
      }
    }
  };

  const linkedInDailyLimit = isSignedIn
    ? (linkedInStatus.daily_limit ?? (isPro ? null : 10))
    : (linkedInStatus.daily_limit ?? 3);
  const linkedInRemaining = isSignedIn
    ? (linkedInStatus.remaining_today ?? (isPro ? null : 10))
    : (linkedInStatus.remaining_today ?? 3);
  const hasSignedInRecruiterLimit = isSignedIn && !isPro && linkedInRemaining === 0;
  const hasAnonymousRecruiterLimit = !isSignedIn && linkedInRemaining === 0;

  const trackSignInIntent = useCallback((source, properties = {}) => {
    rememberSignInIntent(source, properties);
    trackFunnelEvent(
      'sign_in_clicked',
      { source, ...properties },
      { clerkUserId: user?.id, path: trackingPath }
    );
  }, [trackingPath, user?.id]);

  const goToPro = useCallback((source, properties = {}) => {
    trackFunnelEvent(
      'gopro_clicked',
      { source, ...properties },
      { clerkUserId: user?.id, path: trackingPath }
    );
    window.location.href = '/gopro';
  }, [trackingPath, user?.id]);

  const handleLinkedInRecruiters = useCallback((job) => {
    if (!job?.company) return;

    trackFunnelEvent(
      'linkedin_recruiters_requested',
      {
        company: job.company,
        user_tier: isPro ? 'pro' : (isSignedIn ? 'signed_in' : 'signed_out'),
        title: job.title,
      },
      { clerkUserId: user?.id, path: trackingPath }
    );

    if (!isSignedIn) {
      const currentStatus = getAnonymousRecruiterStatus();
      const nextStatus = consumeAnonymousRecruiterSlot();
      if (!nextStatus) {
        setLinkedInStatus(currentStatus);
        setError(
          currentStatus.storage_available === false
            ? 'Create a free account to use recruiter search on this browser.'
              : '0 free searches remaining. Create an account for 10 per day.'
        );
        return;
      }

      setLinkedInStatus(nextStatus);
      window.open(getLinkedInRecruiterUrl(job), '_blank', 'noopener,noreferrer');
      return;
    }

    const targetFallbackUrl = getLinkedInRecruiterUrl(job);
    const recruiterWindow = window.open('', '_blank');
    if (recruiterWindow) {
      recruiterWindow.opener = null;
      showLinkedInOpeningPage(recruiterWindow);
    }

    withLinkedInAccessTimeout(claimLinkedInRecruiterAccess({
      company: job.company,
      title: job.title,
      location: job.location,
      category: job.category,
      source: 'job_card',
    }))
      .then((data) => {
        setLinkedInStatus({
          is_premium: data.is_premium,
          daily_limit: data.daily_limit,
          used_today: data.daily_limit == null || data.remaining_today == null
            ? 0
            : Math.max(data.daily_limit - data.remaining_today, 0),
          remaining_today: data.remaining_today,
        });
        const targetUrl = data.search_url || targetFallbackUrl;
        if (recruiterWindow && !recruiterWindow.closed) {
          recruiterWindow.location.href = targetUrl;
        } else {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        }
      })
      .catch((err) => {
        if (recruiterWindow && !recruiterWindow.closed) {
          recruiterWindow.close();
        }
        const detail = err.response?.data?.detail;
        if (detail?.remaining_today === 0) {
          setLinkedInStatus((prev) => ({
            ...prev,
            daily_limit: detail.daily_limit ?? prev.daily_limit ?? (isSignedIn ? 10 : 3),
            remaining_today: 0,
            is_premium: Boolean(detail.is_premium),
          }));
          setError(
            isSignedIn
              ? '0 free searches remaining. Go Pro for unlimited.'
              : '0 free searches remaining. Create an account for 10 per day.'
          );
          return;
        }
        if (recruiterWindow && !recruiterWindow.closed) {
          recruiterWindow.location.href = targetFallbackUrl;
          return;
        }
        window.open(targetFallbackUrl, '_blank', 'noopener,noreferrer');
      });
  }, [isPro, isSignedIn, trackingPath, user?.id]);

  useEffect(() => {
    if (!hasSignedInRecruiterLimit) return;

    trackFunnelEvent(
      'signed_in_recruiter_limit_seen',
      {
        daily_limit: linkedInDailyLimit,
      },
      { clerkUserId: user?.id, path: trackingPath }
    );
  }, [hasSignedInRecruiterLimit, linkedInDailyLimit, trackingPath, user?.id]);

  const toggleTitle = (title) => {
    const newSelected = new Set(selectedTitles);
    if (newSelected.has(title)) {
      newSelected.delete(title);
    } else {
      newSelected.add(title);
    }
    setSelectedTitles(newSelected);
  };

  const suggestedTitleOptions = useMemo(() => {
    if (!suggestedTitles) return [];
    return Array.isArray(suggestedTitles) ? suggestedTitles : [];
  }, [suggestedTitles]);

  const selectAllSuggested = () => {
    const allTitles = suggestedTitleOptions;
    const newSelected = new Set(selectedTitles);
    allTitles.forEach(title => newSelected.add(title));
    setSelectedTitles(newSelected);
  };

  const clearAllSuggested = () => {
    const allTitles = suggestedTitleOptions;
    const newSelected = new Set(selectedTitles);
    allTitles.forEach(title => newSelected.delete(title));
    setSelectedTitles(newSelected);
  };

  const removeSuggestedTitle = (titleToRemove) => {
    // Remove from suggested titles list (immediate UI update)
    setSuggestedTitles(prev => prev.filter(t => t !== titleToRemove));
    // Also remove from selected if it was selected
    setSelectedTitles(prev => {
      const newSet = new Set(prev);
      newSet.delete(titleToRemove);
      return newSet;
    });
    // Persist dismissal to backend so it survives refresh
    if (isSignedIn) {
      dismissTitle(titleToRemove).catch(err => {
        console.error('Failed to persist title dismissal:', err);
      });
    }
  };

  const addCustomTitle = () => {
    // Sanitize input to prevent XSS
    const newTitle = validator.escape(validator.trim(customTitle));
    if (newTitle && !customTitles.includes(newTitle)) {
      setCustomTitles(prev => [...prev, newTitle]);
    }
    setCustomTitle('');
  };

  const removeCustomTitle = (titleToRemove) => {
    setCustomTitles(prev => prev.filter(t => t !== titleToRemove));
  };

  const allSearchTitles = useMemo(
    () => [...selectedTitles, ...customTitles],
    [selectedTitles, customTitles]
  );

  const normalizedLocation = useMemo(
    () => validator.escape(validator.trim(locationData.location || '')),
    [locationData.location]
  );

  const candidateSearchQuery = useMemo(() => (
    buildCandidateSearchQuery({
      roles: allSearchTitles,
      location: normalizedLocation || 'New York',
    })
  ), [allSearchTitles, normalizedLocation]);

  const copyToClipboard = useCallback(async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(successMessage);
      window.setTimeout(() => setCopyMessage(null), 2500);
    } catch {
      setError('Unable to copy. Please copy the text manually.');
    }
  }, []);

  const getCandidateSearchForJob = useCallback((job) => {
    const roles = getTargetRoles(job?.title, job?.matched_titles || []);
    const skills = [job?.category].filter(Boolean);
    const query = buildCandidateSearchQuery({
      roles,
      location: job?.location || normalizedLocation || 'New York',
      skills,
    });
    return { roles, query, url: getLinkedInCandidateUrl(query) };
  }, [normalizedLocation]);

  const handleFindCandidates = useCallback((job = null) => {
    const query = job ? getCandidateSearchForJob(job).query : candidateSearchQuery;
    if (!query) {
      setError('Add at least one target role before opening LinkedIn search.');
      return;
    }
    trackFunnelEvent(
      'candidate_search_opened',
      {
        source: job ? 'job_signal_card' : 'candidate_search_setup',
        title: job?.title,
        company: job?.company,
      },
      { clerkUserId: user?.id, path: trackingPath }
    );
    window.open(getLinkedInCandidateUrl(query), '_blank', 'noopener,noreferrer');
  }, [candidateSearchQuery, getCandidateSearchForJob, trackingPath, user?.id]);

  const handleCopyCandidateSearch = useCallback((job = null) => {
    const query = job ? getCandidateSearchForJob(job).query : candidateSearchQuery;
    if (!query) {
      setError('Add at least one target role before copying a search string.');
      return;
    }
    copyToClipboard(query, 'Search string copied.');
  }, [candidateSearchQuery, copyToClipboard, getCandidateSearchForJob]);

  const handleCopyOutreach = useCallback((job) => {
    const role = cleanLinkedInQueryPart(job?.title) || '[Role]';
    const company = cleanLinkedInQueryPart(job?.company) || '[Company]';
    const message = `Hey [Name], saw your experience in [X] and thought you might be a strong fit for a ${role} role with ${company}. Open to a quick chat this week?`;
    copyToClipboard(message, 'Outreach message copied.');
    trackFunnelEvent(
      'candidate_outreach_copied',
      { title: job?.title, company: job?.company },
      { clerkUserId: user?.id, path: trackingPath }
    );
  }, [copyToClipboard, trackingPath, user?.id]);

  const getStarterTitlesFromQuery = useCallback(() => {
    const params = new URLSearchParams(pageLocation.search);
    // Only seed search titles from explicit search params.
    // Marketing params like utm_term can contain campaign metadata, not job titles.
    const raw = params.get('titles') || params.get('title') || params.get('q');
    if (!raw) return [];

    return raw
      .split(/[|,]/)
      .map(title => validator.escape(validator.trim(title.replace(/\+/g, ' '))))
      .filter(Boolean)
      .slice(0, 5);
  }, [pageLocation.search]);

  const getStarterLocationFromQuery = useCallback(() => {
    const params = new URLSearchParams(pageLocation.search);
    const raw = params.get('location') || params.get('where') || params.get('city');
    if (!raw) return '';
    return validator.escape(validator.trim(raw.replace(/\+/g, ' ')));
  }, [pageLocation.search]);

  const getSearchSignature = useCallback((titles, normalizedLocation) => {
    return JSON.stringify({
      titles: [...titles].sort(),
      location: normalizedLocation,
      radius: locationData.radius,
      work_types: serializeWorkTypes(workTypeFilter),
      isSignedIn,
      isPro,
    });
  }, [isPro, isSignedIn, locationData.radius, workTypeFilter]);

  useEffect(() => {
    return () => {
      if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!isLoaded) {
      return () => { cancelled = true; };
    }

    if (!isSignedIn) {
      const refreshAnonymousStatus = () => setLinkedInStatus(getAnonymousRecruiterStatus());
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          refreshAnonymousStatus();
        }
      };

      refreshAnonymousStatus();
      window.addEventListener('focus', refreshAnonymousStatus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      const intervalId = window.setInterval(refreshAnonymousStatus, 60000);

      return () => {
        cancelled = true;
        window.removeEventListener('focus', refreshAnonymousStatus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.clearInterval(intervalId);
      };
    }

    getLinkedInRecruiterStatus()
      .then((status) => {
        if (!cancelled) setLinkedInStatus(status);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load recruiter status:', err);
        }
      });

    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    if (!isLoaded || loading || seededStarterSearchRef.current) return;

    const hasExistingTitles = selectedTitles.size > 0 || customTitles.length > 0;
    if (hasExistingTitles) {
      seededStarterSearchRef.current = true;
      return;
    }

    const starterTitles = getStarterTitlesFromQuery();
    if (starterTitles.length > 0) {
      setCustomTitles(starterTitles);
    }
    const starterLocation = getStarterLocationFromQuery();
    if (starterLocation) {
      setLocationData(prev => ({ ...prev, location: starterLocation }));
    }
    seededStarterSearchRef.current = true;
  }, [
    customTitles.length,
    getStarterLocationFromQuery,
    getStarterTitlesFromQuery,
    isLoaded,
    isSignedIn,
    loading,
    selectedTitles.size,
  ]);

  useEffect(() => {
    if (!isLoaded || loading || searching) return;

    if (!normalizedLocation) {
      if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
      lastSearchSignatureRef.current = '';
      setResults([]);
      setTotalCount(0);
      setSearchedTitles([]);
      return;
    }

    if (!isSignedIn && allSearchTitles.length === 0) {
      const signature = JSON.stringify({
        mode: 'anonymous_browse',
        location: normalizedLocation,
        radius: locationData.radius,
        work_types: serializeWorkTypes(workTypeFilter),
      });

      if (signature === lastSearchSignatureRef.current) return;

      if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
      autoSearchTimerRef.current = setTimeout(() => {
        lastSearchSignatureRef.current = signature;
        fbTrack('Search', { search_string: 'jobs', search_mode: 'anonymous_browse' });
        performAnonymousBrowseSearch(normalizedLocation, locationData.radius);
      }, 450);

      return () => {
        if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
      };
    }

    if (allSearchTitles.length === 0) {
      if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
      lastSearchSignatureRef.current = '';
      setResults([]);
      setTotalCount(0);
      setSearchedTitles([]);
      return;
    }

    const signature = getSearchSignature(allSearchTitles, normalizedLocation);

    if (signature === lastSearchSignatureRef.current) return;

    if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
    autoSearchTimerRef.current = setTimeout(() => {
      lastSearchSignatureRef.current = signature;
      fbTrack('Search', { search_string: allSearchTitles.join(', '), search_mode: 'auto' });
      performMultiSearch(allSearchTitles, normalizedLocation, locationData.radius);
    }, 450);

    return () => {
      if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
    };
  }, [
    allSearchTitles,
    getSearchSignature,
    isLoaded,
    isPro,
    isSignedIn,
    loading,
    normalizedLocation,
    locationData.radius,
    searching,
    workTypeFilter,
  ]);

  const handleSearchClick = useCallback(() => {
    const searchLocation = normalizedLocation || 'new york';
    if (!isSignedIn && allSearchTitles.length === 0) {
      lastSearchSignatureRef.current = JSON.stringify({
        mode: 'anonymous_browse',
        location: searchLocation,
        radius: locationData.radius,
        work_types: serializeWorkTypes(workTypeFilter),
      });
      fbTrack('Search', { search_string: 'jobs', search_mode: 'manual_browse' });
      performAnonymousBrowseSearch(searchLocation, locationData.radius);
      return;
    }

    if (allSearchTitles.length > 0) {
      lastSearchSignatureRef.current = getSearchSignature(allSearchTitles, searchLocation);
      fbTrack('Search', { search_string: allSearchTitles.join(', ') });
      performMultiSearch(allSearchTitles, searchLocation, locationData.radius);
    }
  }, [allSearchTitles, getSearchSignature, isSignedIn, normalizedLocation, locationData.radius, workTypeFilter]);

  const handleLocationChange = useCallback((newLocationData) => {
    setLocationData(newLocationData);
    if (isSignedIn) {
      // Signed-in: debounce save to MongoDB so it survives across devices/sessions
      if (locSaveTimer.current) clearTimeout(locSaveTimer.current);
      locSaveTimer.current = setTimeout(() => {
        saveSearchPreferences({
          last_location: newLocationData.location,
          last_radius:   newLocationData.radius,
        }).catch(err => console.error('Failed to save location preference:', err));
      }, 1000);
    } else {
      // Signed-out: localStorage is the only option
      try { localStorage.setItem('jjj_last_location', JSON.stringify(newLocationData)); } catch {}
    }
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImport = async (job) => {
    setImporting(prev => ({ ...prev, [job.source_id]: true }));

    try {
      await importJob(job);
      setImporting(prev => ({ ...prev, [job.source_id]: 'done' }));
      if (onJobImported) onJobImported();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to import this job right now. Please try again in a moment.'));
      setImporting(prev => ({ ...prev, [job.source_id]: false }));
    }
  };

  const handleJobFeedback = async (job, feedback) => {
    // Optimistically record in UI
    setJobFeedback(prev => ({ ...prev, [job.source_id]: feedback }));
    // Thumbs down: remove from results immediately
    if (feedback === 'down') {
      setResults(prev => prev.filter(j => j.source_id !== job.source_id));
    }
    // Persist to backend (fire and forget — don't block UI)
    submitJobFeedback(job.source_id, job.title, job.company, job.location, feedback)
      .catch(err => console.error('Failed to record job feedback:', err));
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    const fmt = (n) => n ? `$${Math.round(n).toLocaleString()}` : '';
    if (min && max) return `${fmt(min)} - ${fmt(max)}`;
    if (min) return `From ${fmt(min)}`;
    if (max) return `Up to ${fmt(max)}`;
    return null;
  };

  const formatContractTime = (value) => {
    const labels = {
      full_time: 'Full time',
      part_time: 'Part time',
      contract: 'Contract',
      permanent: 'Permanent',
      temporary: 'Temporary',
      internship: 'Internship',
    };
    if (!value) return '';
    return labels[value] || value
      .split('_')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // QuickApply state for provider jobs
  const [quickApplyJob, setQuickApplyJob] = useState(null);

  const getScoreColor = (score) => {
    if (score >= 70) return 'success';
    if (score >= 40) return 'warning';
    return 'error';
  };

  // Client-side salary range filter
  // Pass jobs with no salary data, or whose range overlaps the selected range
  const [filterMin, filterMax] = salaryRange;
  const isDefaultRange = filterMin === 0 && filterMax === 200000;
  const isAllWorkTypesSelected = workTypeFilter.size === ALL_WORK_TYPE_IDS.length;

  // Liberal work-type filtering: only reject jobs with an explicit mismatch.
  const detectWorkTypes = (job) => {
    const haystack = [job.location || '', job.title || '', job.description || ''].join(' ').toLowerCase();
    const detected = new Set();
    if (
      haystack.includes('remote') ||
      haystack.includes('work from home') ||
      haystack.includes('wfh') ||
      haystack.includes('distributed') ||
      haystack.includes('anywhere')
    ) detected.add('remote');
    if (haystack.includes('hybrid')) detected.add('hybrid');
    if (
      haystack.includes('onsite') ||
      haystack.includes('on-site') ||
      haystack.includes('in office') ||
      haystack.includes('in-office') ||
      haystack.includes('office-based')
    ) detected.add('onsite');
    return detected;
  };

  const filteredResults = useMemo(() => (
    results.filter(job => {
      if (!isDefaultRange && (job.salary_min || job.salary_max)) {
        const jobMin = job.salary_min || 0;
        const jobMax = job.salary_max || job.salary_min || 0;
        if (!(jobMax >= filterMin && jobMin <= filterMax)) return false;
      }
      if (!isAllWorkTypesSelected) {
        const detectedWorkTypes = detectWorkTypes(job);
        if (detectedWorkTypes.size > 0 && ![...detectedWorkTypes].some(type => workTypeFilter.has(type))) {
          return false;
        }
      }
      return true;
    })
  ), [results, isDefaultRange, filterMin, filterMax, isAllWorkTypesSelected, workTypeFilter]);

  const hasActiveFilters = !isDefaultRange || !isAllWorkTypesSelected;
  const activeWorkTypeLabel = useMemo(() => (
    !isAllWorkTypesSelected
      ? [...workTypeFilter]
          .map(type => (type === 'onsite' ? 'on-site' : type))
          .sort()
          .join(', ')
      : null
  ), [isAllWorkTypesSelected, workTypeFilter]);

  const marketInsights = useMemo(() => {
    const salaryJobs = filteredResults.filter(job => job.salary_min || job.salary_max);
    const minSalary = salaryJobs.length
      ? Math.round(Math.min(...salaryJobs.map(job => job.salary_min || job.salary_max)) / 1000) * 1000
      : filterMin;
    const maxSalary = salaryJobs.length
      ? Math.round(Math.max(...salaryJobs.map(job => job.salary_max || job.salary_min)) / 1000) * 1000
      : filterMax;
    const demand = totalCount >= 50 ? 'High' : totalCount >= 15 ? 'Medium' : 'Low';
    const hiringDifficulty = demand === 'High' && maxSalary >= 160000 ? 'High' : demand === 'Low' ? 'Medium' : 'Medium';

    const avgSalary = salaryJobs.length
      ? formatSalary(minSalary, maxSalary)
      : (isDefaultRange ? '$80,000 - $180,000' : formatSalary(minSalary, maxSalary));

    return {
      avgSalary,
      demand,
      hiringDifficulty,
    };
  }, [filterMin, filterMax, filteredResults, isDefaultRange, totalCount]);

  if (isLoaded && isSignedIn && loading) {
    // Only show loading spinner for signed-in users waiting for profile data
    return (
      <Box sx={layoutPatterns.centerContent}>
        <Box textAlign="center">
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading your profile and finding matching jobs...
          </Typography>
        </Box>
      </Box>
    );
  }

  const hasSuggestedTitles = suggestedTitles && 
    Array.isArray(suggestedTitles) && 
    suggestedTitles.length > 0;

  const customTitleChips = customTitles.length > 0 && (
    <Box sx={{ ...layoutPatterns.flexWrap, ...layoutPatterns.subsection }}>
      {customTitles.map(title => (
        <Chip
          key={title}
          label={title}
          onDelete={() => removeCustomTitle(title)}
          color="secondary"
          variant="filled"
          deleteIcon={<XIcon size={16} style={{ color: 'white' }} />}
        />
      ))}
    </Box>
  );

  const customTitleInput = (
    <Box sx={layoutPatterns.flexRow}>
      <TextField
        fullWidth
        size="small"
        placeholder={isMatcherMode ? 'Type a target role and press Enter...' : 'Type a job title and press Enter...'}
        value={customTitle}
        onChange={(e) => setCustomTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addCustomTitle();
          }
        }}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={addCustomTitle}
        disabled={!customTitle.trim()}
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.75rem',
          minWidth: 52,
          px: 1.5,
          py: 0.55,
          borderColor: 'grey.300',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
        }}
      >
        {isMatcherMode ? '+ Add Role' : 'Add'}
      </Button>
    </Box>
  );

  return (
    <Box>
      {/* Location Picker */}
      <LocationPicker 
        onLocationChange={handleLocationChange}
        initialLocation={locationData.location}
        initialRadius={locationData.radius}
        autoLocateOnMount={false}
      />

      {/* Salary + Work Type Filter */}
      <Card variant="light" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>

          {/* Salary header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <MoneyIcon size={18} />
            <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary' }}>
              Salary Range
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontWeight: 500 }}>
              {sliderRange[0] === 0 && sliderRange[1] === 200000
                ? 'Any salary'
                : `$${(sliderRange[0] / 1000).toFixed(0)}k – ${sliderRange[1] === 200000 ? '$200k+' : `$${(sliderRange[1] / 1000).toFixed(0)}k`}`}
            </Typography>
          </Box>

          {/* Slider — px so thumbs don't clip the card edge */}
          <Box sx={{ px: 1 }}>
            <Slider
              value={sliderRange}
              onChange={(_, val) => {
                setSliderRange(val);
              }}
              onChangeCommitted={(_, val) => {
                setSalaryRange(val);
              }}
              min={0}
              max={200000}
              step={10000}
              marks={[
                { value: 0,      label: 'Any'   },
                { value: 100000, label: '$100k' },
                { value: 200000, label: '$200k+' },
              ]}
              valueLabelDisplay="off"
              sx={{ color: 'primary.main' }}
            />
          </Box>

          {/* Work Type Filter */}
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Typography variant="subtitle2" fontWeight="600" color="text.secondary" sx={{ mb: 1.5 }}>
              Work Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {WORK_TYPE_OPTIONS.map(({ id, label, Icon }) => {
                const active = workTypeFilter.has(id);
                return (
                  <Button
                    key={id}
                    variant={active ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<Icon size={15} />}
                    onClick={() => {
                      setWorkTypeFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        if (next.size === 0) return createAllWorkTypeSet();
                        return next;
                      });
                    }}
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      py: 0.75,
                      ...(active
                        ? { bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }
                        : { borderColor: 'grey.300', color: 'text.secondary', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }),
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Box>
          </Box>

        </CardContent>
      </Card>

      {/* Resume Upload Prompt */}
      {!isMatcherMode && isSignedIn && !profile?.resume_uploaded && (
        <Card variant="light" sx={{ mb: 3, borderColor: 'info.200' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={layoutPatterns.flexRow}>
              <FileTextIcon size={48} strokeWidth={1.5} color="#1e88e5" />
              <Box flexGrow={1}>
                <Typography variant="h6" fontWeight="600" gutterBottom sx={{ color: 'text.primary' }}>
                  Upload Your Resume
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Get AI-powered job title suggestions tailored to your experience
                </Typography>
              </Box>
              <Button component={Link} to="/jobseeker/profile" variant="contained" sx={designSystem.button.contained}>
                Upload Resume →
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Title Selector */}
      <Card variant="light" sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {isSignedIn ? (
            <>
              <Box sx={layoutPatterns.flexRowSpaceBetween}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon size={20} />
                  <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary' }}>
                    {isMatcherMode ? 'Target Roles' : 'Suggested Titles'}
                  </Typography>
                </Box>
                {hasSuggestedTitles ? (
                  <Box sx={layoutPatterns.flexRowEnd}>
                    <Button size="small" onClick={selectAllSuggested}>+ All</Button>
                    <Button size="small" onClick={clearAllSuggested}>− Clear</Button>
                  </Box>
                ) : !isMatcherMode ? (
                  <Button component={Link} to="/jobseeker/profile" size="small" variant="outlined">
                    Complete Profile
                  </Button>
                ) : null}
              </Box>
              {hasSuggestedTitles && (
                <Box sx={{ ...layoutPatterns.flexWrap, ...layoutPatterns.section }}>
                  {suggestedTitleOptions.map(title => (
                    <Chip
                      key={title}
                      label={title}
                      onClick={() => toggleTitle(title)}
                      onDelete={() => removeSuggestedTitle(title)}
                      color={selectedTitles.has(title) ? 'primary' : 'default'}
                      variant={selectedTitles.has(title) ? 'filled' : 'outlined'}
                      deleteIcon={<XIcon size={16} style={{ color: selectedTitles.has(title) ? 'white' : 'rgba(0,0,0,0.6)' }} />}
                    />
                  ))}
                </Box>
              )}

              <Divider sx={layoutPatterns.subsection} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PlusIcon size={20} />
                <Typography variant="subtitle1" fontWeight="600" sx={{ color: 'text.primary' }}>
                  {isMatcherMode ? 'Add Role' : 'Custom Titles'}
                </Typography>
              </Box>
              {customTitleChips}
              {customTitleInput}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FolderIcon size={20} />
                <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary' }}>
                  {isMatcherMode ? 'Target Roles' : 'Job Titles'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 2 }}>
                {isMatcherMode
                  ? 'Add roles recruiters should source for.'
                  : 'Browse jobs right away, or add job titles to narrow the results.'}
              </Typography>
              {customTitleChips}
              {customTitleInput}
            </>
          )}

          {/* Search Button */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSearchClick}
              disabled={searching || (!isSignedIn && !normalizedLocation) || (isSignedIn && allSearchTitles.length === 0)}
              sx={{ ...designSystem.button.contained, minWidth: 160 }}
            >
              {searching ? 'Searching...' : (isMatcherMode ? 'Search Job Signals' : 'Search Jobs')}
            </Button>
            {totalCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {!isSignedIn && searchedTitles.length === 0
                  ? (hasActiveFilters
                      ? `Showing ${filteredResults.length.toLocaleString()} of ${results.length.toLocaleString()} jobs within ${locationData.radius} miles`
                      : `Found ${totalCount.toLocaleString()} jobs within ${locationData.radius} miles`)
                  : hasActiveFilters
                    ? `Showing ${filteredResults.length.toLocaleString()} of ${results.length.toLocaleString()} jobs matching ${searchedTitles.length} titles within ${locationData.radius} miles`
                    : `Found ${totalCount.toLocaleString()} jobs matching ${searchedTitles.length} titles within ${locationData.radius} miles`}
                {activeWorkTypeLabel ? ` • ${activeWorkTypeLabel}` : ''}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {isMatcherMode && (
        <>
          <Card variant="light" sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary', mb: 1 }}>
                Candidate Search Setup
              </Typography>
              <Typography variant="subtitle2" fontWeight="600" color="text.secondary" sx={{ mb: 1 }}>
                Suggested Search
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: candidateSearchQuery ? 'text.primary' : 'text.secondary',
                  mb: 2,
                  overflowWrap: 'anywhere',
                }}
              >
                {candidateSearchQuery || 'Add a target role to generate a LinkedIn people search.'}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<LinkedInIcon size={16} />}
                  onClick={() => handleFindCandidates()}
                  disabled={!candidateSearchQuery}
                  sx={{
                    ...designSystem.button.contained,
                    bgcolor: '#0A66C2',
                    '&:hover': { bgcolor: '#004182' },
                  }}
                >
                  Find Candidates on LinkedIn
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleCopyCandidateSearch()}
                  disabled={!candidateSearchQuery}
                  sx={designSystem.button.outlined}
                >
                  Copy Search
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card variant="light" sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary', mb: 1.5 }}>
                Market Insights
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Avg Salary: ${marketInsights.avgSalary}`} variant="outlined" />
                <Chip label={`Demand: ${marketInsights.demand}`} color="success" variant="outlined" />
                <Chip label={`Hiring Difficulty: ${marketInsights.hiringDifficulty}`} color="warning" variant="outlined" />
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {copyMessage && <Alert severity="success" sx={layoutPatterns.section}>{copyMessage}</Alert>}
      {error && <Alert severity="error" sx={layoutPatterns.section}>{error}</Alert>}

      {searching && (
        <Box sx={{ ...layoutPatterns.centerContent, py: 4 }}>
          <Box textAlign="center">
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {isSignedIn
                ? `Searching ${selectedTitles.size + customTitles.length} ${isMatcherMode ? 'target roles' : 'job titles'}...`
                : 'Loading jobs near you...'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Results */}
      {isMatcherMode && filteredResults.length > 0 && (
        <Typography variant="h6" fontWeight="600" sx={{ mb: 1.5, color: 'text.primary' }}>
          Active Job Signals
        </Typography>
      )}
      {hasActiveFilters && results.length > 0 && filteredResults.length < results.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Showing {filteredResults.length} of {results.length} jobs
          {!isDefaultRange ? ` between ${(filterMin / 1000).toFixed(0)}k – ${filterMax === 200000 ? '$200k+' : `$${(filterMax / 1000).toFixed(0)}k`}` : ''}
          {activeWorkTypeLabel ? `${!isDefaultRange ? ' and' : ' for'} ${activeWorkTypeLabel}` : ''}
        </Typography>
      )}
      <Grid container spacing={2.5}>
        {filteredResults.map(job => {
          const matchedTitles = job.matched_titles || [];
          const hasMatchScore = job.match_display_score != null;
          const hasMatchedTitles = matchedTitles.length > 0;
          const candidateSearch = isMatcherMode ? getCandidateSearchForJob(job) : null;

          return (
          <Grid item xs={12} sm={6} key={job.source_id}>
            <Card 
              variant="interactive"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                {/* Match Score, Source Badge & Feedback */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  {hasMatchScore ? (
                    <Chip
                      label={`${job.match_display_score}% Match`}
                      color={getScoreColor(job.match_display_score)}
                      size="medium"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : null}
                  {job.source === 'platform' && (
                    <Chip 
                      label="Platform"
                      size="small"
                      sx={{ bgcolor: '#8B5A2B', color: 'white', fontWeight: 600 }}
                    />
                  )}
                  {!isMatcherMode && isSignedIn && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleJobFeedback(job, 'up')}
                        title="Good match"
                        sx={{
                          color: jobFeedback[job.source_id] === 'up' ? 'success.main' : 'grey.400',
                          '&:hover': { color: 'success.main', bgcolor: 'success.50' },
                          transition: 'color 0.15s',
                        }}
                      >
                        <ThumbsUpIcon size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleJobFeedback(job, 'down')}
                        title="Not a good match"
                        sx={{
                          color: 'error.main',
                          '&:hover': { color: 'error.dark', bgcolor: 'error.50' },
                          transition: 'color 0.15s',
                        }}
                      >
                        <ThumbsDownIcon size={16} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                
                {/* Job Title */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1,
                    lineHeight: 1.3,
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: 'text.primary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {job.title}
                </Typography>
                
                {/* Company */}
                <Typography 
                  variant="body2" 
                  sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}
                >
                  {job.company}
                </Typography>
                
                {/* Location, Salary, Category */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {job.location && (
                    <Chip 
                      icon={<LocationIcon size={14} />} 
                      label={job.location} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        borderColor: 'grey.300',
                        color: 'text.secondary',
                        bgcolor: 'white',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                  {formatSalary(job.salary_min, job.salary_max) && (
                    <Chip 
                      icon={<MoneyIcon size={14} />} 
                      label={`${formatSalary(job.salary_min, job.salary_max)}${job.salary_is_predicted ? ' (est.)' : ''}`}
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        borderColor: 'grey.300',
                        color: 'text.secondary',
                        bgcolor: 'white',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                  {job.category && (
                    <Chip 
                      icon={<FolderIcon size={14} />} 
                      label={job.category} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        borderColor: 'grey.300',
                        color: 'text.secondary',
                        bgcolor: 'white',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                  {job.contract_time && (
                    <Chip 
                      label={formatContractTime(job.contract_time)} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        borderColor: 'grey.300',
                        color: 'text.secondary',
                        bgcolor: 'white',
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                </Box>

                {/* Matched Titles / Target Roles */}
                {(isMatcherMode || hasMatchedTitles) ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                    {(isMatcherMode ? candidateSearch.roles : matchedTitles).slice(0, 3).map(t => (
                      <Chip 
                        key={t} 
                        label={t} 
                        size="small" 
                        color="primary" 
                        variant="filled"
                        sx={{ fontWeight: 500 }}
                      />
                    ))}
                    {(isMatcherMode ? candidateSearch.roles : matchedTitles).length > 3 && (
                      <Chip 
                        label={`+${(isMatcherMode ? candidateSearch.roles : matchedTitles).length - 3} more`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                ) : null}

                {/* Recruiter Outreach CTA */}
                {!isMatcherMode && job.company && (
                  <Box 
                    sx={{ 
                      borderTop: '1px solid',
                      borderColor: 'grey.200',
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      p: 1.75,
                      mb: 1.75,
                      display: 'flex',
                      gap: 1.25,
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box component="span" sx={{ mt: 0.125, flexShrink: 0, color: 'primary.main', opacity: 0.9 }}>
                      <LinkedInIcon size={16} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="600" sx={{ color: 'text.primary', mb: 0.375 }}>
                        Message LinkedIn Recruiters
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.55, fontSize: '0.875rem', color: 'text.secondary', mb: 1.25 }}>
                        {isPro
                          ? 'Unlimited recruiter outreach unlocked for this company.'
                          : isSignedIn
                            ? `${linkedInRemaining} free searches remaining. Go Pro for unlimited.`
                            : linkedInRemaining > 0
                              ? `${linkedInRemaining} free searches remaining. Create an account for 10 per day.`
                              : '0 free searches remaining. Create an account for 10 per day.'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {linkedInDailyLimit == null || linkedInRemaining > 0 ? (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<MessageCircleIcon size={16} />}
                            onClick={() => handleLinkedInRecruiters(job)}
                            sx={{
                              ...designSystem.button.contained,
                              bgcolor: '#0A66C2',
                              '&:hover': {
                                bgcolor: '#004182',
                              },
                            }}
                          >
                            Message LinkedIn Recruiters
                          </Button>
                        ) : isSignedIn ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => goToPro('recruiter_limit_card', { company: job.company })}
                            sx={designSystem.button.contained}
                          >
                            Go Pro for Unlimited
                          </Button>
                        ) : (
                          <SignInButton mode="modal">
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => trackSignInIntent('recruiter_limit_card', { company: job.company, title: job.title })}
                              sx={designSystem.button.contained}
                            >
                              Create Free Account for 10 / Day
                            </Button>
                          </SignInButton>
                        )}
                        {!isPro && isSignedIn && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => goToPro('recruiter_secondary_cta', { company: job.company })}
                            sx={designSystem.button.outlined}
                          >
                            Go Pro Unlimited
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Description */}
                {job.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.6,
                    }}
                  >
                    {job.description}
                  </Typography>
                )}
              </CardContent>
              
              {/* Actions */}
              <Divider sx={designSystem.divider} />
              <CardActions sx={{ p: 2, pt: 1.75, gap: 1.25 }}>
                {isMatcherMode ? (
                  <>
                    <Button
                      variant="contained"
                      size="medium"
                      fullWidth
                      startIcon={<LinkedInIcon size={16} />}
                      onClick={() => handleFindCandidates(job)}
                      sx={{
                        ...designSystem.button.contained,
                        bgcolor: '#0A66C2',
                        '&:hover': { bgcolor: '#004182' },
                      }}
                    >
                      Find Candidates
                    </Button>
                    <Button
                      variant="outlined"
                      size="medium"
                      fullWidth
                      startIcon={<FileTextIcon size={16} />}
                      onClick={() => handleCopyOutreach(job)}
                      sx={designSystem.button.outlined}
                    >
                      Copy Outreach Message
                    </Button>
                  </>
                ) : job.source === 'platform' ? (
                  <>
                    {isSignedIn ? (
                      <Button
                        variant="contained"
                        size="medium"
                        fullWidth
                        onClick={() => setQuickApplyJob(job)}
                        sx={designSystem.button.contained}
                      >
                        Express Interest
                      </Button>
                    ) : (
                      <SignInButton mode="modal">
                        <Button
                          variant="contained"
                          size="medium"
                          fullWidth
                          onClick={() => trackSignInIntent('apply_lock', { company: job.company, title: job.title })}
                          sx={designSystem.button.contained}
                        >
                          Create Free Account to Apply
                        </Button>
                      </SignInButton>
                    )}
                    {isSignedIn && (
                      <Button
                        variant="outlined"
                        size="medium"
                        fullWidth
                        onClick={() => handleImport(job)}
                        disabled={importing[job.source_id]}
                        sx={designSystem.button.outlined}
                      >
                        {importing[job.source_id] === 'done' ? 'Saved ✓' : 
                         importing[job.source_id] ? 'Saving...' : 'Save Job'}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button 
                      href={job.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      variant="outlined"
                      size="medium"
                      fullWidth
                      sx={designSystem.button.outlined}
                    >
                      View Job
                    </Button>
                    {isSignedIn ? (
                      <Button
                        variant="contained"
                        size="medium"
                        fullWidth
                        onClick={() => handleImport(job)}
                        disabled={importing[job.source_id]}
                        sx={designSystem.button.contained}
                      >
                        {importing[job.source_id] === 'done' ? 'Saved ✓' : 
                         importing[job.source_id] ? 'Saving...' : 'Save Job'}
                      </Button>
                    ) : (
                      <SignInButton mode="modal">
                        <Button
                          variant="contained"
                          size="medium"
                          fullWidth
                          onClick={() => trackSignInIntent('save_job_lock', { company: job.company, title: job.title })}
                          sx={designSystem.button.contained}
                        >
                          Save Job
                        </Button>
                      </SignInButton>
                    )}
                  </>
                )}
              </CardActions>
            </Card>
          </Grid>
        )})}
      </Grid>

      {!isMatcherMode && hasSignedInRecruiterLimit && (
        <Card sx={{ mt: 3, ...designSystem.card.light, p: 3 }}>
          <Typography variant="h6" fontWeight="700" sx={{ color: 'text.primary', mb: 1 }}>
            0 free searches remaining
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
            Go Pro for unlimited recruiter searches and unlimited access across sessions and devices.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => goToPro('recruiter_daily_limit', { daily_limit: linkedInDailyLimit })}
              sx={designSystem.button.contained}
            >
              Go Pro for Unlimited
            </Button>
          </Box>
        </Card>
      )}

      {!isMatcherMode && hasAnonymousRecruiterLimit && (
        <Card sx={{ mt: 3, ...designSystem.card.light, p: 3 }}>
          <Typography variant="h6" fontWeight="700" sx={{ color: 'text.primary', mb: 1 }}>
            0 free searches remaining
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
            Create a free account for 10 recruiter searches per day, or Go Pro for unlimited recruiter access.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <SignInButton mode="modal">
              <Button
                variant="contained"
                onClick={() => trackSignInIntent('anonymous_recruiter_daily_limit', { daily_limit: linkedInDailyLimit })}
                sx={designSystem.button.contained}
              >
                Create Free Account for 10 / Day
              </Button>
            </SignInButton>
            <Button
              variant="outlined"
              onClick={() => goToPro('anonymous_recruiter_daily_limit_secondary', { daily_limit: linkedInDailyLimit })}
              sx={designSystem.button.outlined}
            >
              Go Pro Unlimited
            </Button>
          </Box>
        </Card>
      )}

      {/* Empty State */}
      {!searching && filteredResults.length === 0 && !error && !loading && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            {!isDefaultRange && results.length > 0 ? (
              `No jobs found in that salary range — try widening the filter.`
            ) : workTypeFilter.size > 0 && results.length > 0 ? (
              `No jobs found for the selected work type${workTypeFilter.size > 1 ? 's' : ''} — try widening the filter.`
            ) : !isSignedIn ? (
              'We could not load jobs yet. Try updating your location above.'
            ) : selectedTitles.size > 0 || customTitles.length > 0 ? (
              isMatcherMode
                ? 'Adjust your target roles, location, or filters to update the signals.'
                : 'Adjust your titles, location, or filters to update the search.'
            ) : profile?.title ? (
              `Try searching for "${profile.title}" jobs above.`
            ) : (
              isMatcherMode ? 'Add a target role above to start sourcing.' : 'Enter a job title above to start searching.'
            )}
          </Typography>
        </Box>
      )}

      {/* Primary CTA */}
      {!isPro && !isSignedIn && (
        <Card 
          sx={{ 
            mt: 4, 
            textAlign: 'center', 
            p: 4,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2, color: 'secondary.main' }}>
              <StarIcon size={48} strokeWidth={1.5} />
            </Box>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Create a Free Account First
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Save jobs, unlock match insights, and get 10 recruiter searches per day. Upgrade to Pro for unlimited recruiter access and outreach tools.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              <SignInButton mode="modal">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => trackSignInIntent('anonymous_bottom_cta')}
                  sx={{ fontWeight: 700 }}
                >
                  Create Free Account
                </Button>
              </SignInButton>
              <Button
                variant="outlined"
                size="large"
                onClick={() => goToPro('anonymous_bottom_cta_secondary')}
                sx={{ fontWeight: 700 }}
              >
                See Pro
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!isPro && isSignedIn && (
        <Card 
          sx={{ 
            mt: 4, 
            textAlign: 'center', 
            p: 4,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 2, color: 'secondary.main' }}>
              <StarIcon size={48} strokeWidth={1.5} />
            </Box>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Unlock Unlimited Recruiter Access
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Go Pro for unlimited recruiter searches, message LinkedIn recruiters, and more!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => goToPro('signed_in_bottom_cta')}
              sx={{ 
                bgcolor: 'white', 
                color: 'secondary.main',
                fontWeight: 700,
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.95)',
                  transform: 'scale(1.02)',
                }
              }}
            >
              Go Pro →
            </Button>
          </CardContent>
        </Card>
      )}

      {/* QuickApply Dialog for Platform Jobs */}
      <QuickApply
        open={Boolean(quickApplyJob)}
        onClose={() => setQuickApplyJob(null)}
        job={quickApplyJob}
      />
    </Box>
  );
}

export default JobSearch;
